import { Decimal } from "../money/decimal.js";
import { Money, roundPreservingSum } from "../money/money.js";
import { calculateVatDocument } from "../money/vat.js";
const QUANTITY_SCALE = 3;
const UNIT_PRICE_SCALE = 4;
function fail(index, message) {
    throw new RangeError(index === null ? `calculateInvoice: ${message}` : `calculateInvoice: satır ${index + 1}: ${message}`);
}
function hasAtMostScale(value, scale) {
    return value.round(scale).eq(value);
}
function discountOf(base, discount, index) {
    if (!discount)
        return Money.ZERO;
    if (discount.kind === "RATE") {
        const percent = Decimal.from(discount.percent);
        if (percent.isNegative() || percent.gt(100))
            fail(index, `iskonto oranı 0–100 aralığında olmalı (${percent.toString()})`);
        return base.percentage(percent);
    }
    if (discount.amount.isNegative())
        fail(index, "iskonto tutarı negatif olamaz");
    if (discount.amount.gt(base)) {
        fail(index, `iskonto (${discount.amount.toMajorString()}) tutarı (${base.toMajorString()}) aşamaz`);
    }
    return discount.amount;
}
export function calculateInvoice(input) {
    if (input.lines.length === 0)
        fail(null, "en az bir satır gerekli");
    // 1–2. Satır tutarı ve satır iskontosu
    const prepared = input.lines.map((line, index) => {
        const quantity = Decimal.from(line.quantity);
        const unitPrice = Decimal.from(line.unitPrice);
        const rate = Decimal.from(line.vatRatePercent);
        if (!quantity.isPositive())
            fail(index, "miktar sıfırdan büyük olmalı");
        if (!hasAtMostScale(quantity, QUANTITY_SCALE))
            fail(index, "miktar en çok 3 ondalık hane taşır");
        if (unitPrice.isNegative())
            fail(index, "birim fiyat negatif olamaz");
        if (!hasAtMostScale(unitPrice, UNIT_PRICE_SCALE))
            fail(index, "birim fiyat en çok 4 ondalık hane taşır");
        if (rate.isNegative() || rate.gt(100))
            fail(index, `KDV oranı 0–100 aralığında olmalı (${rate.toString()})`);
        if (line.exempt && !rate.isZero())
            fail(index, "istisna satırında KDV oranı 0 olmalı");
        const withholding = line.withholding ?? null;
        if (withholding) {
            const { numerator, denominator } = withholding;
            if (!Number.isSafeInteger(numerator) ||
                !Number.isSafeInteger(denominator) ||
                numerator <= 0 ||
                denominator <= 0) {
                fail(index, "tevkifat payı ve paydası pozitif tam sayı olmalı");
            }
            if (numerator > denominator)
                fail(index, "tevkifat payı paydasını aşamaz");
            if (line.exempt)
                fail(index, "istisna satırına tevkifat uygulanamaz");
        }
        const amount = Money.fromUnitPrice(unitPrice, quantity);
        const lineDiscount = discountOf(amount, line.discount, index);
        return { rate, withholding, amount, lineDiscount, afterLine: amount.sub(lineDiscount) };
    });
    // 3. Belge iskontosu — satırlara iskonto sonrası tutarlarıyla orantılı dağıtılır
    const afterLineTotal = Money.sum(prepared.map((line) => line.afterLine));
    const documentDiscount = discountOf(afterLineTotal, input.documentDiscount, null);
    const shares = documentDiscount.isZero() || afterLineTotal.isZero()
        ? prepared.map(() => Money.ZERO)
        : documentDiscount.allocate(prepared.map((line) => Decimal.from(line.afterLine.minor)));
    // 4. KDV
    const bases = prepared.map((line, index) => line.afterLine.sub(shares[index]));
    const vatDocument = calculateVatDocument(bases.map((amount, index) => ({ amount, ratePercent: prepared[index].rate })), input.mode);
    // 5. Tevkifat — grup toplam KDV'si üzerinden, satırlara en büyük kalanla
    const withheld = prepared.map(() => Money.ZERO);
    const groups = new Map();
    prepared.forEach((line, index) => {
        if (!line.withholding)
            return;
        const key = line.withholding.code?.trim() || `${line.withholding.numerator}/${line.withholding.denominator}`;
        const group = groups.get(key) ?? { spec: line.withholding, indexes: [] };
        if (group.spec.numerator !== line.withholding.numerator ||
            group.spec.denominator !== line.withholding.denominator) {
            fail(index, `aynı tevkifat kodu (${key}) farklı oranlarla kullanılamaz`);
        }
        group.indexes.push(index);
        groups.set(key, group);
    });
    const withholdings = [];
    for (const [key, group] of [...groups.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))) {
        const ratio = Decimal.from(group.spec.numerator).div(group.spec.denominator);
        const vats = group.indexes.map((index) => vatDocument.lines[index].vat);
        const groupVat = Money.sum(vats);
        const groupWithheld = groupVat.mul(ratio);
        const parts = roundPreservingSum(vats.map((vat) => Decimal.from(vat.minor).mul(ratio)), groupWithheld.minor);
        group.indexes.forEach((lineIndex, position) => {
            withheld[lineIndex] = Money.fromMinor(parts[position]);
        });
        withholdings.push({
            key,
            code: group.spec.code?.trim() || null,
            numerator: group.spec.numerator,
            denominator: group.spec.denominator,
            vat: groupVat,
            withheld: groupWithheld,
        });
    }
    // 6. Satır ve belge toplamları
    const lines = prepared.map((line, index) => {
        const { net, vat, gross } = vatDocument.lines[index];
        const discountInMode = line.lineDiscount.add(shares[index]);
        // KDV hariç modda iskonto zaten KDV hariçtir; dâhil modda KDV'den arındırılır. `grossNet − discountNet = net` korunur.
        const discountNet = input.mode === "VAT_EXCLUSIVE"
            ? discountInMode
            : Money.fromDecimalMajor(discountInMode.toDecimal().div(Decimal.ONE.add(line.rate.div(100))));
        return {
            amount: line.amount,
            lineDiscount: line.lineDiscount,
            documentDiscountShare: shares[index],
            grossNet: net.add(discountNet),
            discountNet,
            net,
            vat,
            gross,
            withheld: withheld[index],
            payable: gross.sub(withheld[index]),
        };
    });
    const totalWithheld = Money.sum(withheld);
    return {
        mode: input.mode,
        lines,
        byRate: vatDocument.byRate,
        withholdings,
        totals: {
            grossNet: Money.sum(lines.map((line) => line.grossNet)),
            discountNet: Money.sum(lines.map((line) => line.discountNet)),
            net: vatDocument.totals.net,
            vat: vatDocument.totals.vat,
            gross: vatDocument.totals.gross,
            withheld: totalWithheld,
            payable: vatDocument.totals.gross.sub(totalWithheld),
        },
    };
}
//# sourceMappingURL=calculate.js.map